import React, { useState, useEffect } from 'react';
import { Modal } from 'shards-react';
import ModalEditHeader from '../../common/ModalEditHeader';
import ModalEditBodyDelete from './ModalEditBodyDelete';
import ModalEditBody from './ModalEditBody';
import ModalEditFooter from '../../common/ModalEditFooter';
import { useTranslation } from 'react-i18next';
import useLocales from '../../../hooks/useLocales';
import useUserRoles from '../../../hooks/useUserRoles';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { closeDialog, createDeliveryLocation, deleteDeliveryLocation, updateDeliveryLocation } from '../../../redux/slices/deliveryLocation';
import { STEEL_MAKER } from '../../../constants';
import type { CreateDeliveryLocationParams, DeleteDeliveryLocationParams, DeliveryLocationType, UpdateDeliveryLocationParams } from '../../../types/deliveryLocation';
import type { SteelMaker } from '../../../types/common';
import type { OptionsType } from '../../../types/options';

const ModalEditDeliveryLocation = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { currentLang } = useLocales();
  const { t } = useTranslation();
  const { isMaintenanceadmin } = useUserRoles();
  const { dialogMode, isDialogOpen, deliveryLocation, deliveryLocations } = useSelector((state: RootState) => state.deliveryLocation);
  const { steelMaker } = useSelector((state: RootState) => state.options);
  const [selectedSteelMaker, setSelectedSteelMaker] = useState<OptionsType<SteelMaker> | null>(null);
  const [isDirtyMaker, setIsDirtyMaker] = useState<boolean>(false);
  const [code, setCode] = useState<string>('');
  const [isDirtyCode, setIsDirtyCode] = useState<boolean>(false);
  const [deliveryName, setDeliveryName] = useState<string>('');
  const [isDirtyName, setIsDirtyName] = useState<boolean>(false);
  const [dialogTitle, setDialogTitle] = useState<string>('');
  const [buttonLabel, setButtonLabel] = useState<string>('');
  const [deleteFlag, setDeleteFlag] = useState<boolean>(false);
  const [isUniqueCode, setIsUniqueCode] = useState<boolean | undefined>(undefined);
  const optionsFilter = (options: OptionsType<SteelMaker>[]): OptionsType<SteelMaker>[] => 
    options.filter((option) => 
      option.code === STEEL_MAKER.NIPPONSTEEL || 
      option.code === STEEL_MAKER.JFESTEEL
  );
  // SIDE EFFECT
  // ================================================================================

  /**
   * 確認用
   */
  // useEffect(() => {
  //   console.log(isUniqueCode);
  // }, [isUniqueCode]);

  /**
   * 新規作成モード以外では初期値を入れておく
   */
  useEffect(() => {
    if (dialogMode !== 'CREATE' && deliveryLocation) {
      const maker: OptionsType<SteelMaker> | undefined = steelMaker.find((maker) => maker.code === deliveryLocation.steelMaker);
      setSelectedSteelMaker(maker ?? null);
      setCode(deliveryLocation.code);
      setDeliveryName(deliveryLocation.deliveryName);
    } else {
      // do nothing
    }
  }, [dialogMode, deliveryLocation]);

  /**
   * モードに併せてタイトルとボタン名を設定
   */
  useEffect(() => {
    if (dialogMode === 'CREATE') {
      setDialogTitle(t('deliveryLocation.dialog.titleCreate'));
      setButtonLabel(t('dialog.create'));
    } else if (dialogMode === 'UPDATE') {
      setDialogTitle(t('deliveryLocation.dialog.titleUpdate'));
      setButtonLabel(t('dialog.update'));
    } else if (dialogMode === 'DELETE') {
      setDialogTitle(t('deliveryLocation.dialog.titleDelete'));
      setButtonLabel(t('dialog.delete'));
    } else {
      setDialogTitle('');
      setButtonLabel('');
    }
  }, [dialogMode]);

  /**
   * 組織名の重複チェック
   */
  useEffect(() => {
    if (dialogMode === 'CREATE' || dialogMode === 'UPDATE') {
      // 新規作成モードと更新モードではdeliveryLocationsに同じコードのものがないかチェック
      const sameDeliveryLocationCode: DeliveryLocationType | undefined = deliveryLocations
        ? deliveryLocations.find((location) => {
            if (dialogMode === 'CREATE') {
              // 新規作成時はコードと鉄鋼Mが同じものがないか確認する
              const isDuplicate: boolean = location.code === code && location.steelMaker === selectedSteelMaker?.code;
              return isDuplicate;
            } else {
              // 更新時はidをチェックして「idは違うがコードと名前が同じ」ものがないか確認する
              const isDuplicate: boolean = location.code === code && location.steelMaker === selectedSteelMaker?.code && location.id !== deliveryLocation?.id;
              return isDuplicate;
            }
          })
        : undefined;
      if (sameDeliveryLocationCode !== undefined) {
        setIsUniqueCode(false);
      } else {
        setIsUniqueCode(true);
      }
    } else if (dialogMode === 'DELETE') {
      // 削除モードでは何もチェックしない
      setIsUniqueCode(undefined);
    } else {
      setIsUniqueCode(undefined);
    }
  }, [code, deliveryName, selectedSteelMaker]);

  // EVENT HANDLER
  // ================================================================================

  const resetModal = (): void => {
    dispatch(closeDialog());
    setSelectedSteelMaker(null);
    setCode('');
    setDeliveryName('');
    setIsDirtyMaker(false);
    setIsDirtyCode(false);
    setIsDirtyName(false);
    setDeleteFlag(false);
    setIsUniqueCode(undefined);
  };

  /**
   * 受渡場所コードを変更した時
   */
  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setIsDirtyCode(true);
    // console.log(event);
    const newDeliveryLocationCode: string = event.target.value;
    setCode(newDeliveryLocationCode);
  };

  /**
   * 受渡場所名を変更した時
   */
  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setIsDirtyName(true);
    // console.log(event);
    const newDeliveryName: string = event.target.value;
    setDeliveryName(newDeliveryName);
  };

  /**
   * 鉄鋼Mを変更した時
   */
  const handleSteelMakerChange = (steelMaker: OptionsType<SteelMaker>): void => {
    setIsDirtyMaker(true);
    setSelectedSteelMaker(steelMaker);
  };

  /**
   * モーダルでキャンセルボタンを押した時
   */
  const handleCancel = (): void => {
    resetModal();
  };

  /**
   * モーダルでOKボタンを押した時
   */
  const handleSave = (): void => {
    // 新規作成時
    if (dialogMode === 'CREATE' && steelMaker && code && deliveryName) {
      const params: CreateDeliveryLocationParams = {
        steelMaker: selectedSteelMaker.code,
        code,
        deliveryName,
      };
      dispatch(createDeliveryLocation(params));

      // 更新時
    } else if (dialogMode === 'UPDATE' && deliveryLocation && code && deliveryName) {
      const params: UpdateDeliveryLocationParams = {
        id: deliveryLocation.id,
        steelMaker: selectedSteelMaker.code,
        code,
        deliveryName,
      };
      dispatch(updateDeliveryLocation(params));

      // 削除時
    } else if (dialogMode === 'DELETE' && deliveryLocation) {
      const params: DeleteDeliveryLocationParams = {
        id: deliveryLocation.id,
      };
      dispatch(deleteDeliveryLocation(params));

      // その他
    } else {
      // do nothing
    }
    resetModal();
  };

  /**
   * モーダルでDeleteチェックをクリックした時
   */
  const handleToggleDelete = (): void => {
    setDeleteFlag(!deleteFlag);
  };

  // VIEW
  // ================================================================================

  if (!isMaintenanceadmin) {
    return <></>;
  }

  return (
    <Modal id="modalEditDeliveryLocation" open={isDialogOpen} maxWidth={'xs'} fullWidth={true} centered={true} toggle={handleCancel}>
      <ModalEditHeader iconName={'edit_location_alt'} title={dialogTitle} />
      {/* 新規作成時・更新時 */}
      {(dialogMode === 'CREATE' || dialogMode === 'UPDATE') && (
        <ModalEditBody
          labelCode={t('deliveryLocation.code')}
          labelName={t('deliveryLocation.name')}
          code={code}
          name={deliveryName}
          disabledCode={false}
          disabledName={false}
          invalidCode={isDirtyCode && (code === '' || (code !== '' && !isUniqueCode))}
          invalidName={isDirtyName && deliveryName === ''}
          isNotUnique={isUniqueCode === false}
          isNotUniqueText={t('dialog.errorDup')}
          onChangeCode={handleCodeChange}
          onChangeName={handleNameChange}
          selectId="selectSteelMaker"
          selectClass="w-100 mb-3"
          steelMaker={selectedSteelMaker}
          options={steelMaker}
          placeholder={t('steelMaker')}
          language={currentLang.value}
          optionsFilter={optionsFilter}
          onChangeSelect={handleSteelMakerChange}
        />
      )}
      {/* 削除時 */}
      {dialogMode === 'DELETE' && <ModalEditBodyDelete description={t('deliveryLocation.dialog.card1')} label={t('deliveryLocation.dialog.card2')} name={deliveryName} />}
      <ModalEditFooter
        mode={dialogMode}
        theme={dialogMode === 'DELETE' ? 'danger' : 'primary'}
        disabled={(dialogMode === 'CREATE' && !isUniqueCode) || (dialogMode === 'UPDATE' && !isUniqueCode) || (dialogMode === 'DELETE' && !deleteFlag) || !selectedSteelMaker || !code || !deliveryName}
        labelCancel={t('dialog.cancel')}
        labelSave={buttonLabel}
        labelCheckBox={t('dialog.validDelete')}
        deleteFlag={deleteFlag}
        onCancel={handleCancel}
        onSave={handleSave}
        onToggle={handleToggleDelete}
      />
    </Modal>
  );
};

export default ModalEditDeliveryLocation;
